import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  Settings, 
  ShoppingCart, 
  User, 
  Save, 
  Search,
  Mail,
  MessageSquare,
  CheckCircle,
  XCircle,
  Info,
  Bell
} from 'lucide-react';

const NotificationSettings = () => {
  const [activeTab, setActiveTab] = useState('order');
  const [orderSettings, setOrderSettings] = useState({
    smsEnabled: true,
    emailEnabled: true,
    notificationPriority: 'both',
    retryOnFailure: true,
    maxRetries: 3
  });
  const [customerPreferences, setCustomerPreferences] = useState({
    smsEnabled: true,
    emailEnabled: true,
    preferredChannel: 'both',
    orderUpdates: true,
    deliveryUpdates: true
  });
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchOrder, setSearchOrder] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');

  useEffect(() => {
    if (activeTab === 'order' && selectedOrderId) {
      fetchOrderSettings(selectedOrderId);
    } else if (activeTab === 'customer' && selectedCustomerId) {
      fetchCustomerPreferences(selectedCustomerId);
    }
  }, [activeTab, selectedOrderId, selectedCustomerId]);

  const fetchOrderSettings = async (orderId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-settings/order/${orderId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOrderSettings({
          smsEnabled: data.smsEnabled !== false,
          emailEnabled: data.emailEnabled !== false,
          notificationPriority: data.notificationPriority || 'both',
          retryOnFailure: data.retryOnFailure !== false,
          maxRetries: data.maxRetries || 3
        });
      } else {
        // Use defaults if settings don't exist
        setOrderSettings({
          smsEnabled: true,
          emailEnabled: true,
          notificationPriority: 'both',
          retryOnFailure: true,
          maxRetries: 3
        });
      }
    } catch (error) {
      console.error('Error fetching order settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerPreferences = async (customerId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-settings/customer/${customerId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCustomerPreferences({
          smsEnabled: data.smsEnabled !== false,
          emailEnabled: data.emailEnabled !== false,
          preferredChannel: data.preferredChannel || 'both',
          orderUpdates: data.orderUpdates !== false,
          deliveryUpdates: data.deliveryUpdates !== false
        });
      } else {
        // Use defaults
        setCustomerPreferences({
          smsEnabled: true,
          emailEnabled: true,
          preferredChannel: 'both',
          orderUpdates: true,
          deliveryUpdates: true
        });
      }
    } catch (error) {
      console.error('Error fetching customer preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrderSettings = async () => {
    if (!selectedOrderId) {
      alert('Please select an order first');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-settings/order/${selectedOrderId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(orderSettings)
        }
      );

      if (response.ok) {
        alert('✅ Settings saved successfully!');
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.error || 'Failed to save settings'}`);
      }
    } catch (error) {
      console.error('Error saving order settings:', error);
      alert('❌ Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomerPreferences = async () => {
    if (!selectedCustomerId) {
      alert('Please select a customer first');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/notification-settings/customer/${selectedCustomerId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(customerPreferences)
        }
      );

      if (response.ok) {
        alert('✅ Preferences saved successfully!');
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.error || 'Failed to save preferences'}`);
      }
    } catch (error) {
      console.error('Error saving customer preferences:', error);
      alert('❌ Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const searchOrders = async () => {
    if (!searchOrder || searchOrder.trim() === '') {
      setOrders([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/orders?search=${encodeURIComponent(searchOrder.trim())}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const foundOrders = data.orders || [];
        setOrders(foundOrders);
        
        if (foundOrders.length === 0) {
          alert('No orders found matching your search.');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to search orders' }));
        alert(`Error: ${errorData.error || 'Failed to search orders'}`);
        setOrders([]);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      alert('Failed to search orders. Please try again.');
      setOrders([]);
    }
  };

  const searchCustomers = async () => {
    if (!searchCustomer || searchCustomer.trim() === '') {
      setCustomers([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/orders?search=${encodeURIComponent(searchCustomer.trim())}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const orders = data.orders || [];
        
        const customerMap = new Map();
        orders.forEach(order => {
          if (order.customer && !customerMap.has(order.customer.id)) {
            customerMap.set(order.customer.id, order.customer);
          }
        });
        
        const foundCustomers = Array.from(customerMap.values());
        setCustomers(foundCustomers);
        
        if (foundCustomers.length === 0) {
          alert('No customers found matching your search.');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to search customers' }));
        alert(`Error: ${errorData.error || 'Failed to search customers'}`);
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      alert('Failed to search customers. Please try again.');
      setCustomers([]);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Notification Settings</h2>
        <p className="text-gray-600 text-sm">
          Manage how customers receive order updates via SMS and Email
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('order')}
            className={`flex-1 px-6 py-3 text-center font-medium transition-colors ${
              activeTab === 'order'
                ? 'text-[#d44100] border-b-2 border-[#d44100] bg-orange-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <ShoppingCart className="h-5 w-5 inline mr-2" />
            Order Settings
          </button>
          <button
            onClick={() => setActiveTab('customer')}
            className={`flex-1 px-6 py-3 text-center font-medium transition-colors ${
              activeTab === 'customer'
                ? 'text-[#d44100] border-b-2 border-[#d44100] bg-orange-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <User className="h-5 w-5 inline mr-2" />
            Customer Preferences
          </button>
        </div>
      </div>

      {/* Order Settings - Simplified */}
      {activeTab === 'order' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Order Notification Settings</h3>
            <p className="text-sm text-gray-600">
              Control how notifications are sent for a specific order
            </p>
          </div>

          {/* Order Search */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="h-4 w-4 inline mr-1" />
              Find Order
            </label>
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Enter order number (e.g., DPC-123456)..."
                  value={searchOrder}
                  onChange={(e) => setSearchOrder(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchOrders()}
                  className="w-full pl-4 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                />
              </div>
              <button
                onClick={searchOrders}
                className="px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] transition-colors"
              >
                Search
              </button>
            </div>

            {orders.length > 0 && (
              <div className="mt-3">
                <select
                  value={selectedOrderId}
                  onChange={(e) => {
                    setSelectedOrderId(e.target.value);
                    if (e.target.value) fetchOrderSettings(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                >
                  <option value="">Select an order...</option>
                  {orders.map(order => (
                    <option key={order.id} value={order.id}>
                      {order.orderNumber} - {order.customer?.fullName || 'N/A'}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Found {orders.length} order{orders.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            {searchOrder && searchOrder.trim() !== '' && orders.length === 0 && !loading && (
              <p className="mt-2 text-sm text-gray-500">No orders found. Try a different search term.</p>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading settings...</div>
          ) : selectedOrderId ? (
            <div className="space-y-6">
              {/* Simple Toggle Section */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start mb-4">
                  <Bell className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-800">Notification Channels</h4>
                    <p className="text-xs text-gray-600">Choose how to notify the customer</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={orderSettings.smsEnabled}
                      onChange={(e) => setOrderSettings({ ...orderSettings, smsEnabled: e.target.checked })}
                      className="h-5 w-5 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <MessageSquare className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="font-medium text-gray-700">SMS Notifications</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Send text messages to customer's phone</p>
                    </div>
                  </label>

                  <label className="flex items-center p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={orderSettings.emailEnabled}
                      onChange={(e) => setOrderSettings({ ...orderSettings, emailEnabled: e.target.checked })}
                      className="h-5 w-5 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="font-medium text-gray-700">Email Notifications</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Send emails to customer's email address</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Priority Selection */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Priority
                </label>
                <select
                  value={orderSettings.notificationPriority}
                  onChange={(e) => setOrderSettings({ ...orderSettings, notificationPriority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                >
                  <option value="both">Send Both SMS & Email</option>
                  <option value="sms">SMS First (Email as backup)</option>
                  <option value="email">Email First (SMS as backup)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Choose which notification method to prioritize
                </p>
              </div>

              {/* Retry Settings */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start mb-3">
                  <Info className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">Automatic Retry</h4>
                    <p className="text-xs text-gray-600">Automatically retry if notification fails</p>
                  </div>
                </div>
                
                <label className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={orderSettings.retryOnFailure}
                    onChange={(e) => setOrderSettings({ ...orderSettings, retryOnFailure: e.target.checked })}
                    className="h-4 w-4 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable automatic retry on failure</span>
                </label>

                {orderSettings.retryOnFailure && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Maximum Retry Attempts</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={orderSettings.maxRetries}
                      onChange={(e) => setOrderSettings({ ...orderSettings, maxRetries: parseInt(e.target.value) || 3 })}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveOrderSettings}
                disabled={saving}
                className="w-full px-4 py-3 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-colors shadow-md"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No order selected</p>
              <p className="text-sm text-gray-500 mt-1">Search and select an order above to configure its notification settings</p>
            </div>
          )}
        </div>
      )}

      {/* Customer Preferences - Simplified */}
      {activeTab === 'customer' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Customer Notification Preferences</h3>
            <p className="text-sm text-gray-600">
              Set default notification preferences for a customer
            </p>
          </div>

          {/* Customer Search */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="h-4 w-4 inline mr-1" />
              Find Customer
            </label>
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by customer name or email..."
                  value={searchCustomer}
                  onChange={(e) => setSearchCustomer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchCustomers()}
                  className="w-full pl-4 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                />
              </div>
              <button
                onClick={searchCustomers}
                className="px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] transition-colors"
              >
                Search
              </button>
            </div>

            {customers.length > 0 && (
              <div className="mt-3">
                <select
                  value={selectedCustomerId}
                  onChange={(e) => {
                    setSelectedCustomerId(e.target.value);
                    if (e.target.value) fetchCustomerPreferences(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                >
                  <option value="">Select a customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.fullName || customer.name || 'Unknown'} - {customer.email || 'No email'}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Found {customers.length} customer{customers.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            {searchCustomer && searchCustomer.trim() !== '' && customers.length === 0 && !loading && (
              <p className="mt-2 text-sm text-gray-500">No customers found. Try a different search term.</p>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading preferences...</div>
          ) : selectedCustomerId ? (
            <div className="space-y-6">
              {/* Notification Channels */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start mb-4">
                  <Bell className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-800">Preferred Notification Methods</h4>
                    <p className="text-xs text-gray-600">How this customer wants to be notified</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={customerPreferences.smsEnabled}
                      onChange={(e) => setCustomerPreferences({ ...customerPreferences, smsEnabled: e.target.checked })}
                      className="h-5 w-5 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <MessageSquare className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="font-medium text-gray-700">SMS Notifications</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Customer can receive text messages</p>
                    </div>
                  </label>

                  <label className="flex items-center p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={customerPreferences.emailEnabled}
                      onChange={(e) => setCustomerPreferences({ ...customerPreferences, emailEnabled: e.target.checked })}
                      className="h-5 w-5 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="font-medium text-gray-700">Email Notifications</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Customer can receive emails</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Update Preferences */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Types
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customerPreferences.orderUpdates}
                      onChange={(e) => setCustomerPreferences({ ...customerPreferences, orderUpdates: e.target.checked })}
                      className="h-4 w-4 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Order Status Updates</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customerPreferences.deliveryUpdates}
                      onChange={(e) => setCustomerPreferences({ ...customerPreferences, deliveryUpdates: e.target.checked })}
                      className="h-4 w-4 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Delivery Updates</span>
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveCustomerPreferences}
                disabled={saving}
                className="w-full px-4 py-3 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-colors shadow-md"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No customer selected</p>
              <p className="text-sm text-gray-500 mt-1">Search and select a customer above to configure their preferences</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
